import { useState } from 'react'
import type { AccountDetail } from '../../../api/accounts'
import { api } from '../../../api/client'
import { formatDateTime } from '../../../lib/format'
import { useAccountAction } from './useAccountAction'

/** One line for an event's data: the note text, the ticket, or its fields. */
function describe(e: AccountDetail['events'][number]) {
	if (e.kind === 'note') return String(e.data.text ?? '')
	if (e.kind === 'ticket') return String(e.data.ticket ?? '')
	return Object.entries(e.data)
		.filter(([, v]) => v !== null && v !== undefined)
		.map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
		.join(' · ')
}

/** The account's history (audit log and support notes), newest first, with a box for notes and ticket links. */
export function TimelineCard({ accountId, events }: { accountId: string; events: AccountDetail['events'] }) {
	const [text, setText] = useState('')
	const add = useAccountAction(accountId, (kind: 'notes' | 'tickets') =>
		api
			.post(`/api/v1/admin/accounts/${accountId}/${kind}`, kind === 'notes' ? { text } : { ticket: text })
			.then(() => setText('')),
	)

	return (
		<section className="card stack">
			<h2 style={{ margin: 0 }}>Timeline</h2>
			<form className="row" onSubmit={(e) => (e.preventDefault(), text.trim() && add.mutate('notes'))}>
				<input
					aria-label="Note or ticket"
					placeholder="Support note, or a ticket URL"
					value={text}
					onChange={(e) => setText(e.target.value)}
					style={{ flex: 1 }}
				/>
				<button type="submit" disabled={!text.trim()}>
					Add note
				</button>
				<button type="button" disabled={!text.trim()} onClick={() => add.mutate('tickets')}>
					Link ticket
				</button>
			</form>
			{add.isError && <p className="error small">{add.error.message}</p>}
			<div className="table-scroll">
				<table>
					<tbody>
						{events.map((e) => (
							<tr key={e.id}>
								<td className="small muted" style={{ whiteSpace: 'nowrap' }}>
									{formatDateTime(e.at)}
								</td>
								<td className="small">
									<strong>{e.kind.replaceAll('_', ' ')}</strong> <span className="muted">by {e.actor}</span>
								</td>
								<td className="small">{describe(e)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	)
}
